'use client';

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Breadcrumbs } from '@/components/ui-components';
import { CheckIcon, DeleteIcon } from '@/components/icons';
import { i18n } from '@/config/i18n';
import { theme } from '@/config/theme';
import { routes } from '@/config/routes';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
} from '@/services/notificationService';
import type { Notification as NotificationType } from '@/types/notification';
import { useToast } from '@/context/ToastContext';
import { format } from 'date-fns';

export interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialNotifications?: NotificationType[];
  onNotificationsUpdate?: (notifications: NotificationType[]) => void;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-start;
  justify-content: flex-end;
  z-index: 9999;
  padding: 84px 24px 20px 20px;
  animation: ${({ $isOpen }) => ($isOpen ? 'fadeIn 0.2s ease' : 'none')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  
  /* Blur overlay that excludes navbar (60px height) */
  &::before {
    content: '';
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: -1;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 104px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
  
  @keyframes slideIn {
    from {
      transform: translateY(-10px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 48px);
    max-height: calc(100vh - 84px);
  }
`;

const HeaderSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
  flex-shrink: 0;
`;

const BreadcrumbWrapper = styled.div`
  padding:  16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const MarkAllReadLink = styled.button`
  background: none;
  border: none;
  color: #0088FF;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  white-space: nowrap;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray[50]};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary.accent};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotificationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.gray[50]};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray[300]};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.gray[400]};
  }
`;

const NotificationItem = styled.div<{ $isRead: boolean; $clickable?: boolean }>`
  padding: 14px 16px;
  min-height:100px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  background: ${({ $isRead, theme }) => ($isRead ? theme.colors.white : theme.colors.gray[50])};
  transition: background-color 0.2s;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray[50]};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const UnreadIndicator = styled.div`
  position: absolute;
  right: 24px;
  top: 16px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0088FF;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div<{ $isRead: boolean }>`
  font-size: 14px;
  font-weight: ${({ $isRead }) => $isRead ? 500 : 700};
  color: ${({ theme }) => theme.colors.gray[900]};
  margin-bottom: 8px;
  line-height: 1.4;
      word-break: break-word;
`;

const NotificationDescription = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray[900]};
  margin-bottom: 16px;
  line-height: 1.4;
      word-break: break-word;
`;

const NotificationTimestamp = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray[550]};
  line-height: 1.4;
      word-break: break-word;
`;

const NotificationActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: flex-end;
  gap: 16px;
  flex-shrink: 0;
  margin-left: auto;
`;

const ActionButton = styled.button<{ $variant?: 'success' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: ${({ $variant, theme }) => {
    if ($variant === 'success') return theme.colors.status.success;
    if ($variant === 'danger') return theme.colors.status.error;
    return theme.colors.gray[600];
  }};
  
  &:hover {
    background: ${({ $variant, theme }) => {
      if ($variant === 'success') return theme.colors.status.successBg;
      if ($variant === 'danger') return theme.colors.status.errorBg;
      return theme.colors.gray[100];
    }};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary.accent};
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray[500]};
`;

const EmptyStateText = styled.div`
  font-size: 14px;
  margin-top: 8px;
`;

const LoadingState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray[500]};
  font-size: 14px;
`;

const formatNotificationTitle = (type: string, title: string, origin?: string): string => {
  const typeLabel = i18n.notifications.types[type as keyof typeof i18n.notifications.types] || type;
  const originText = origin ? ` (${origin})` : '';
  return `${title}${originText}`;
};

const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    // Format: "Dec 17, 2025, 06:26:55 AM UTC"
    return format(date, "MMM d, yyyy, hh:mm:ss a 'UTC'");
  } catch {
    return timestamp;
  }
};

/**
 * Get navigation URL for a notification based on its type and data
 */
const getNotificationUrl = (notification: NotificationType): string | null => {
  const { type, data } = notification;

  if (!data) {
    return null;
  }

  switch (type) {
    case 'alert':
      // Navigate to alert details if alert_id is available
      if (data.alert_id) {
        return routes.triage.alertDetail(data.alert_id);
      }
      return null;

    case 'incident':
      // Navigate to incident details if incident_id is available
      if (data.incident_id) {
        return routes.triage.detail(data.incident_id);
      }
      return null;

    case 'threshold':
      // If alert_id exists, go to alert details
      if (data.alert_id) {
        return routes.triage.alertDetail(data.alert_id);
      }
      // Otherwise, if site_id exists, go to monitor details
      if (data.site_id) {
        return routes.monitors.detail(data.site_id);
      }
      return null;

    default:
      return null;
  }
};

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose,
  initialNotifications,
  onNotificationsUpdate,
}) => {
  const { showToast } = useToast();
  const [notifications, setNotifications] = useState<NotificationType[]>(initialNotifications || []);
  const [loading, setLoading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  // Update notifications when initialNotifications prop changes
  useEffect(() => {
    if (initialNotifications) {
      setNotifications(initialNotifications);
    }
  }, [initialNotifications]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await getNotifications();
      const fetchedNotifications = response.notifications || [];
      setNotifications(fetchedNotifications);
      // Notify parent component of updates
      if (onNotificationsUpdate) {
        onNotificationsUpdate(fetchedNotifications);
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: i18n.notifications.error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Only fetch if no initial notifications provided
      if (!initialNotifications || initialNotifications?.length === 0) {
        loadNotifications();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId: number) => {
    const idKey = String(notificationId);
    if (processingIds.has(idKey)) return;
    
    setProcessingIds((prev) => new Set(prev).add(idKey));
    try {
      await markNotificationAsRead(notificationId);
      const updatedNotifications = notifications.map((n) =>
        n.id === notificationId ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n
      );
      setNotifications(updatedNotifications);
      // Notify parent component of updates
      if (onNotificationsUpdate) {
        onNotificationsUpdate(updatedNotifications);
      }
      showToast({
        type: 'success',
        message: i18n.notifications.markReadSuccess,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: i18n.notifications.error,
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(idKey);
        return next;
      });
    }
  };

  const handleDelete = async (notificationId: number) => {
    const idKey = String(notificationId);
    if (processingIds.has(idKey)) return;
    
    setProcessingIds((prev) => new Set(prev).add(idKey));
    try {
      await deleteNotification(notificationId);
      const updatedNotifications = notifications.filter((n) => n.id !== notificationId);
      setNotifications(updatedNotifications);
      // Notify parent component of updates
      if (onNotificationsUpdate) {
        onNotificationsUpdate(updatedNotifications);
      }
      showToast({
        type: 'success',
        message: i18n.notifications.deleteSuccess,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: i18n.notifications.error,
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(idKey);
        return next;
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    if (processingIds.has('all')) return;
    
    setProcessingIds((prev) => new Set(prev).add('all'));
    try {
      await markAllNotificationsAsRead();
      const now = new Date().toISOString();
      const updatedNotifications = notifications.map((n) => ({ ...n, status: 'read' as const, read_at: now }));
      setNotifications(updatedNotifications);
      // Notify parent component of updates
      if (onNotificationsUpdate) {
        onNotificationsUpdate(updatedNotifications);
      }
      showToast({
        type: 'success',
        message: i18n.notifications.markAllReadSuccess,
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: i18n.notifications.error,
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete('all');
        return next;
      });
    }
  };

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const hasUnreadNotifications = notifications.some((n) => n.status === 'unread');

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <HeaderSection>
          <BreadcrumbWrapper>
            <Breadcrumbs
              items={[{ label: i18n.notifications.title }]}
              style={{ padding: '0' }}
            />
              <MarkAllReadLink
                  onClick={handleMarkAllAsRead}
                  disabled={!hasUnreadNotifications || processingIds.has('all')}
                >
                  {i18n.notifications.markAllAsRead}
                </MarkAllReadLink>
          </BreadcrumbWrapper>
        </HeaderSection>

        <NotificationsList>
          {loading ? (
            <LoadingState>{i18n.notifications.loading}</LoadingState>
          ) : notifications?.length === 0 ? (
            <EmptyState>
              <EmptyStateText>{i18n.notifications.emptyState}</EmptyStateText>
            </EmptyState>
          ) : (
            notifications.map((notification) => {
              const isUnread = notification.status === 'unread';
              const isProcessing = processingIds.has(String(notification.id));
              const notificationUrl = getNotificationUrl(notification);
              
              const handleNotificationClick = () => {
                if (notificationUrl) {
                  onClose(); // Close modal before navigation
                  window.open(notificationUrl, '_blank');
                }
              };

              const handleActionClick = (e: React.MouseEvent) => {
                e.stopPropagation(); // Prevent navigation when clicking action buttons
              };

              return (
                <NotificationItem 
                  key={notification.id} 
                  $isRead={!isUnread}
                  $clickable={!!notificationUrl}
                  onClick={handleNotificationClick}
                >
                  {isUnread && <UnreadIndicator />}
                  <NotificationContent>
                    <NotificationTitle $isRead={!isUnread}>
                      {formatNotificationTitle(
                        notification.type,
                        notification.title,
                        notification.origin
                      )}
                    </NotificationTitle>
                    <NotificationDescription>
                      {notification.body}
                    </NotificationDescription>
                    <NotificationTimestamp>
                      {formatTimestamp(notification.created_at)}
                    </NotificationTimestamp>
                  </NotificationContent>
                  <NotificationActions onClick={handleActionClick}>
                    {isUnread && (
                      <ActionButton
                        $variant="success"
                        onClick={(e) => {
                          handleActionClick(e);
                          handleMarkAsRead(notification.id);
                        }}
                        disabled={isProcessing}
                        aria-label={i18n.notifications.actions.markAsRead}
                        title={i18n.notifications.actions.markAsRead}
                      >
                        <CheckIcon />
                      </ActionButton>
                    )}
                    <ActionButton
                      $variant="danger"
                      onClick={(e) => {
                        handleActionClick(e);
                        handleDelete(notification.id);
                      }}
                      disabled={isProcessing}
                      aria-label={i18n.notifications.actions.delete}
                      title={i18n.notifications.actions.delete}
                    >
                      <DeleteIcon />
                    </ActionButton>
                  </NotificationActions>
                </NotificationItem>
              );
            })
          )}
        </NotificationsList>
      </ModalContainer>
    </Overlay>
  );
};

export default NotificationsModal;

    